import { useState } from "react";
import { Link } from "react-router";
import { PackagePlus } from "lucide-react";

import { DeploymentTable } from "../components/DeploymentTable.jsx";
import { PageHeader } from "../components/PageHeader.jsx";
import { StatSummary } from "../components/StatSummary.jsx";
import { EmptyState, Spinner, useToast } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { ProjectCard } from "./Projects.jsx";

/**
 * The landing page.
 *
 * It used to be a flat grid of project cards, which told you nothing you could not
 * get from the Projects page. Three bands instead: the state of the box, what has
 * happened lately, and then the projects — so landing here answers "is everything
 * all right" before you have clicked anything.
 */
export function Overview() {
    const [projects, setProjects] = useState(null);
    const [deployments, setDeployments] = useState([]);
    const [system, setSystem] = useState(null);
    const [busy, setBusy] = useState(null);
    const toast = useToast();

    const loadProjects = () => api.projects.list().then(setProjects).catch(() => setProjects([]));
    const loadDeployments = () => api.deployments.list({ limit: 10 }).then(setDeployments).catch(() => {});
    // The cards carry each project's last deployment, so they follow both.
    useLive(["projects", "deployments"], loadProjects);
    useLive(["deployments"], loadDeployments);
    useLive(["system"], () => api.system.info().then(setSystem).catch(() => {}));
    const load = () => {
        loadProjects();
        loadDeployments();
    };

    const deploy = async (slug) => {
        setBusy(slug);
        try {
            const result = await api.projects.deploy(slug);
            toast(`${slug} #${result.number} started`);
            load();
        } catch (error) {
            toast(error.message, "error");
        } finally {
            setBusy(null);
        }
    };

    if (!projects) return <Spinner className="muted" />;

    const iconOf = (slug) => projects.find((project) => project.slug === slug)?.favicon?.hash ?? null;
    const failing = deployments.filter((d) => d.status === "failed" || d.status === "rolled-back").length;

    return (
        <>
            <PageHeader title="Overview" meta={`${projects.length} project${projects.length === 1 ? "" : "s"}`} />

            <Health system={system} projects={projects} failing={failing} />

            <section className="mt-6">
                <div className="mb-2 flex items-baseline justify-between">
                    <h2 className="section-title">Recent activity</h2>
                    <Link to="/deployments" className="link text-xs">All deployments</Link>
                </div>

                <DeploymentTable
                    deployments={deployments}
                    project
                    iconOf={iconOf}
                    caption="Recent deployments"
                    empty={<div className="card"><EmptyState title="Nothing deployed yet" /></div>}
                />
            </section>

            <section className="mt-6">
                <div className="mb-2 flex items-baseline justify-between">
                    <h2 className="section-title">Projects</h2>
                    {projects.length > 0 ? <Link to="/projects" className="link text-xs">Manage</Link> : null}
                </div>

                {projects.length === 0 ? (
                    <div className="card">
                        <EmptyState
                            icon={PackagePlus}
                            title="No projects yet"
                            hint="Onboard one with the scaling-ops skill, or POST /api/projects. Ops creates the GitLab webhook and points it here."
                        />
                    </div>
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {projects.slice(0, 6).map((project) => (
                            <ProjectCard key={project.slug} project={project} busy={busy === project.slug} onDeploy={() => deploy(project.slug)} />
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}

/** The four numbers worth knowing before anything else. */
function Health({ system, projects, failing }) {
    const offTarget = (system?.runtimes ?? [])
        .filter((bucket) => bucket.major !== "26")
        .reduce((total, bucket) => total + bucket.count, 0);

    const stats = [
        {
            label: "Processes",
            value: system ? `${system.processes.online}/${system.processes.total}` : "—",
            tone: system && system.processes.stopped > 0 ? "amber" : null
        },
        {
            label: "Recent failures",
            value: String(failing),
            tone: failing > 0 ? "red" : null
        },
        {
            label: "Off Node 26",
            value: system ? String(offTarget) : "—",
            tone: offTarget > 0 ? "amber" : null
        },
        {
            label: "Disk free",
            value: system?.disk ? `${(system.disk.freeBytes / 1e9).toFixed(0)} GB` : "—"
        }
    ];

    return <StatSummary stats={stats} />;
}
